import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, withKeys as _withKeys, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, renderSlot as _renderSlot, withModifiers as _withModifiers } from "vue";
const _hoisted_1 = {
    class: "relative",
    "data-testid": "search-bar-container"
};
const _hoisted_2 = {
    key: 0,
    "data-testid": "search-dropdown",
    class: "absolute left-0 right-0 mt-1 z-50"
};
const _hoisted_3 = {
    id: "search-results-list",
    role: "listbox",
    "data-testid": "search-results-list",
    class: "max-h-96 overflow-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg divide-y divide-gray-100 dark:divide-gray-700"
};
const _hoisted_4 = {
    key: 0,
    "data-testid": "search-loading",
    class: "px-4 py-3 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"
};
const _hoisted_5 = ["onMousedown"];
const _hoisted_6 = ["innerHTML"];
const _hoisted_7 = {
    key: 1,
    "data-testid": "search-no-results",
    class: "px-4 py-3 text-sm text-gray-500 dark:text-gray-400"
};
import { ref, watch, computed } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useLogger } from "@/shared/composables/useLogger";
import { SearchInput } from "@eudonet/opal";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SearchBar',
    props: {
        searchFn: {},
        getItemKey: {},
        placeholder: { default: "Search" },
        searchingText: { default: "Searching…" },
        noResultsText: { default: "No results" },
        minChars: { default: 3 },
        limit: { default: 10 }
    },
    emits: ["select"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // Logger
        const logger = useLogger();
        const searchQuery = ref("");
        const isFocused = ref(false);
        const results = ref([]);
        const isLoading = ref(false);
        const hasSearched = ref(false);
        const showDropdown = computed(() => isFocused.value && (isLoading.value || searchQuery.value.trim().length >= props.minChars));
        // Debounced search to avoid too many API calls
        const debouncedSearch = useDebounceFn(async (query) => {
            const q = query.trim();
            if (q.length < props.minChars) {
                results.value = [];
                isLoading.value = false;
                hasSearched.value = false;
                return;
            }
            isLoading.value = true;
            try {
                const data = await props.searchFn(q);
                results.value = Array.isArray(data) ? data.slice(0, props.limit) : [];
            }
            catch {
                results.value = [];
            }
            finally {
                isLoading.value = false;
                hasSearched.value = true;
            }
        }, 250);
        const handleSearch = () => {
            debouncedSearch(searchQuery.value);
        };
        const handleEnterSearch = () => {
            const query = searchQuery.value.trim();
            if (query) {
                logger.debug("Searching for:", query);
            }
            if (results.value.length > 0) {
                selectResult(results.value[0]);
            }
        };
        const handleFocus = () => {
            isFocused.value = true;
        };
        const handleBlur = () => {
            isFocused.value = false;
        };
        const clearSearch = () => {
            searchQuery.value = "";
            results.value = [];
            hasSearched.value = false;
        };
        // SearchInput leaves Escape to the consumer while its listbox is expanded
        const handleEscape = (event) => {
            if (!showDropdown.value)
                return;
            event.preventDefault();
            clearSearch();
        };
        // Watch for changes in search query
        watch(searchQuery, (newValue) => {
            if (newValue.length === 0) {
                logger.debug("Search cleared");
                // Handle empty search - maybe clear results
                results.value = [];
                hasSearched.value = false;
            }
        });
        function highlight(text) {
            const q = searchQuery.value.trim();
            if (!q)
                return text;
            try {
                const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                const regex = new RegExp(`(${escaped})`, "ig");
                return text.replace(regex, "<strong>$1</strong>");
            }
            catch {
                return text;
            }
        }
        function selectResult(item) {
            emit("select", item);
            isFocused.value = false;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(SearchInput), {
                    id: "search-input",
                    modelValue: searchQuery.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((searchQuery).value = $event)),
                    "data-testid": "search-input",
                    placeholder: __props.placeholder,
                    rounded: "full",
                    "full-width": "",
                    size: "md",
                    "aria-label": __props.placeholder,
                    "aria-controls": "search-results-list",
                    "aria-expanded": showDropdown.value,
                    autocomplete: "off",
                    onInput: handleSearch,
                    onKeydown: [
                        _withKeys(handleEnterSearch, ["enter"]),
                        _withKeys(handleEscape, ["escape"])
                    ],
                    onFocus: handleFocus,
                    onBlur: handleBlur,
                    onClear: clearSearch
                }, null, 8 /* PROPS */, ["modelValue", "placeholder", "aria-label", "aria-expanded"]),
                (showDropdown.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("ul", _hoisted_3, [
                            (isLoading.value)
                                ? (_openBlock(), _createElementBlock("li", _hoisted_4, [
                                    _cache[1] || (_cache[1] = _createElementVNode("svg", {
                                        class: "animate-spin w-4 h-4 text-blue-600",
                                        xmlns: "http://www.w3.org/2000/svg",
                                        viewBox: "0 0 24 24",
                                        fill: "none",
                                        "aria-hidden": "true"
                                    }, [
                                        _createElementVNode("circle", {
                                            class: "opacity-25",
                                            cx: "12",
                                            cy: "12",
                                            r: "10",
                                            stroke: "currentColor",
                                            "stroke-width": "4"
                                        }),
                                        _createElementVNode("path", {
                                            class: "opacity-75",
                                            fill: "currentColor",
                                            d: "M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        })
                                    ], -1 /* CACHED */)),
                                    _createElementVNode("span", null, _toDisplayString(__props.searchingText), 1 /* TEXT */)
                                ]))
                                : _createCommentVNode("v-if", true),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(results.value, (item, idx) => {
                                return (_openBlock(), _createElementBlock("li", {
                                    key: __props.getItemKey ? __props.getItemKey(item) : idx,
                                    "data-testid": "search-result-item"
                                }, [
                                    _createElementVNode("button", {
                                        type: "button",
                                        role: "option",
                                        "data-testid": "search-result-button",
                                        class: "w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-hidden",
                                        onMousedown: _withModifiers(($event) => (selectResult(item)), ["prevent"])
                                    }, [
                                        _renderSlot(_ctx.$slots, "result", {
                                            item: item,
                                            query: searchQuery.value,
                                            highlight: highlight
                                        }, () => [
                                            _createElementVNode("div", {
                                                class: "text-sm text-gray-900 dark:text-gray-100 truncate",
                                                innerHTML: highlight(String(item))
                                            }, null, 8 /* PROPS */, _hoisted_6)
                                        ])
                                    ], 40 /* PROPS, NEED_HYDRATION */, _hoisted_5)
                                ]));
                            }), 128 /* KEYED_FRAGMENT */)),
                            (!isLoading.value && hasSearched.value && results.value.length === 0)
                                ? (_openBlock(), _createElementBlock("li", _hoisted_7, _toDisplayString(__props.noResultsText), 1 /* TEXT */))
                                : _createCommentVNode("v-if", true)
                        ])
                    ]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
